<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        data-test="open-shared-funds"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg
          class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      <button
        v-if="canEditClaim"
        type="button"
        class="btn-icon btn-icon-default"
        @click.stop="createCost"
      >
        <svg
          class="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>

      <span class="capitalize">
        {{ $t('ClaimCertifiedCosts.SharedFundRow.title') }}
      </span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': totalCosts.variance !== 0 }"
    >
      {{ australianCurrency(totalCosts.variance) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <SharedFundCostRow
      v-for="cost in costs.filter((item) => item.shared_fund)"
      :key="cost.id"
      :claim="$props.claim"
      :shared-fund-cost="cost"
      :shared-fund="cost.shared_fund"
      :show-percentages="$props.showPercentages"
    />

    <tr
      v-if="costs.length === 0"
      class="bg-gray-50"
    >
      <td
        colspan="100%"
        class="text-center p-4 text-gray-500"
      >
        <p>
          {{ $t('ClaimCertifiedCosts.SharedFundRow.empty') }}
        </p>

        <button
          v-if="canEditClaim"
          type="button"
          class="btn btn--secondary-blue mt-2"
          @click="createCost"
        >
          {{ $t('ClaimCertifiedCosts.SharedFundRow.attach') }}
        </button>
      </td>
    </tr>

    <SharedFundCostModal
      v-if="show"
      :claim="claim"
      @close="show = false"
    />
  </template>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import SharedFundCost from '@/models/SharedFundCost';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { computed, ref } from 'vue';
import SharedFundCostModal from './SharedFundCostModal.vue';
import SharedFundCostRow from './SharedFundCostRow.vue';

const props = defineProps<{
  costs: SharedFundCost[];
  claim: Claim;
  showPercentages?: boolean;
}>();

const sharedFundsStore = useSharedFundsStore();

const attachedSharedFunds = computed(() => {
  return sharedFundsStore.models.whereHas(
    'costs',
    (query) => {
      query.where('claim_id', props.claim.id);
    },
  )
    .with(
      'costs',
      (query) => {
        query.where('claim_id', props.claim.id);
      },
    )
    .with('project_group', (query) => query.with('projects'))
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  attachedSharedFunds.value.forEach((sharedFund) => {
    totals.budget += sharedFund.budget;
    totals.previous += sharedFund.previous(props.claim);
    const sharedFundCost = sharedFund.costs[0];

    if(sharedFundCost) {
      totals.currentClaim += sharedFundCost.current_claim ?? 0;
      totals.currentCertified += sharedFundCost.current_certified ?? 0;
      totals.variance += (sharedFundCost.current_claim ?? 0) - (sharedFundCost.current_certified ?? 0);
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);
const show = ref(false);

const createCost = () => {
  show.value = true;
  open.value = true;
};

const signedParams = useSignedData();

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (props.claim.status === ClaimStatus.contractor_claim ||
      props.claim.status === ClaimStatus.draft_contractor);
});
</script>

<style scoped></style>
